// Builds account and reference suggestion collections used by the sheet command entry UI.
import { useMemo } from "react";
import type { SheetAccountEntry } from "../../../types/sheetDocument";
import type { AccountSuggestionOption } from "../components/SheetCommandEntry";
import {
  buildCommentReferenceHelpByReference,
  buildCommentReferenceSuggestions,
} from "../commentUtils";
import type { TAccountCatalogItem } from "../types";

interface UseSheetSuggestionsParams {
  tAccounts: SheetAccountEntry[];
  taccountCatalog: TAccountCatalogItem[];
  displayLetterBySequence: Map<number, string>;
  uberCommentsByLetter: Record<string, string>;
}

export default function useSheetSuggestions({
  tAccounts,
  taccountCatalog,
  displayLetterBySequence,
  uberCommentsByLetter,
}: UseSheetSuggestionsParams) {
  const accountSuggestions = useMemo<AccountSuggestionOption[]>(
    () => taccountCatalog
      .map((account) => ({
        value: account.name,
        label: account.alias ? `${account.name} / ${account.alias}` : account.name,
      }))
      .sort((left, right) => left.label.localeCompare(right.label)),
    [taccountCatalog],
  );

  const lineItemReferenceSuggestions = useMemo(() => {
    const topLevelReferences = tAccounts
      .flatMap((account) => account.entries)
      .filter((entry) => !entry.isBeginningBalance)
      .map((entry) => {
        const displayReference = (displayLetterBySequence.get(entry.sequence) ?? entry.letter).toLowerCase();
        return displayReference.split(".")[0];
      });

    return Array.from(new Set(topLevelReferences)).sort((left, right) => left.localeCompare(right));
  }, [displayLetterBySequence, tAccounts]);

  const commentLineItemReferenceSuggestions = useMemo(() => {
    const references = tAccounts
      .flatMap((account) => account.entries)
      .filter((entry) => !entry.isBeginningBalance)
      .map((entry) => (displayLetterBySequence.get(entry.sequence) ?? entry.letter).toLowerCase());

    return buildCommentReferenceSuggestions(references);
  }, [displayLetterBySequence, tAccounts]);

  const editLineItemReferenceSuggestions = useMemo(() => {
    const references = tAccounts
      .flatMap((account) => account.entries)
      .filter((entry) => !entry.isBeginningBalance)
      .map((entry) => (displayLetterBySequence.get(entry.sequence) ?? entry.letter).toLowerCase());

    return Array.from(new Set(references)).sort((left, right) => left.localeCompare(right));
  }, [displayLetterBySequence, tAccounts]);

  const commentReferenceHelpByReference = useMemo(
    () => buildCommentReferenceHelpByReference(commentLineItemReferenceSuggestions, uberCommentsByLetter),
    [commentLineItemReferenceSuggestions, uberCommentsByLetter],
  );

  return {
    accountSuggestions,
    lineItemReferenceSuggestions,
    commentLineItemReferenceSuggestions,
    editLineItemReferenceSuggestions,
    commentReferenceHelpByReference,
  };
}
